/**
 * Profiles and discovery (module 10).
 *
 *  - `/public/companies/{slug}` — a published company page; readable without
 *    signing in (rate-limited). Never a person's phone/email/ID, a document or
 *    a contract value — the server leaves them out.
 *  - `/directory/companies` — search published companies, paged on the server.
 *  - `/organizations/{org}/…` — the company's page, registrations, portfolio,
 *    shortlist, projects, clients, workload, role matrix, fee tracker and the
 *    project invitations addressed to it.
 *  - `/projects/{id}/company-invitations` — invite a directory company.
 *  - `/me/professional-profile`, `/me/onboarding` — the person.
 *
 * All online: nothing here is queued.
 */
import { z } from 'zod';

import { api, type ApiMeta } from './client';

const str = z
  .string()
  .nullable()
  .catch('')
  .default('')
  .transform((v) => v ?? '');
const nullableStr = z.string().nullable().catch(null).default(null);
const num = z.coerce.number().catch(0).default(0);
const nullableNum = z.number().nullable().catch(null).default(null);
const strings = z.array(z.string()).catch([]).default([]);
const key = (idempotencyKey: string) => ({ idempotencyKey });

export const imageSchema = z.object({ id: z.string(), url: z.string(), thumb_url: z.string() });
export type PublicImage = z.infer<typeof imageSchema>;

export const badgesSchema = z.object({
  verified_company: z.boolean().catch(false).default(false),
  nca_registered: z.boolean().catch(false).default(false),
  insured: z.boolean().catch(false).default(false),
  tax_compliant: z.boolean().catch(false).default(false),
});
export type Badges = z.infer<typeof badgesSchema>;

export const reviewSchema = z.object({
  /** client | main_contractor — the side, never the person or the project. */
  source: str,
  stage: str,
  average: num,
  comment: str,
  month: str,
});
export const ratingSummarySchema = z.object({
  average: nullableNum,
  count: num,
  dimensions: z.record(z.string(), z.number().nullable()).catch({}).default({}),
  reviews: z.array(reviewSchema).catch([]).default([]),
});
export type RatingSummary = z.infer<typeof ratingSummarySchema>;

export const portfolioItemSchema = z.object({
  id: z.string(),
  title: str,
  county: str,
  location: str,
  year: nullableNum,
  value_band: str,
  role: str,
  scope: str,
  photos: z.array(imageSchema).catch([]).default([]),
  testimonial: str,
  testimonial_by: str,
  // The company's own view only.
  visible: z.boolean().optional(),
  sort_order: z.number().optional(),
  photo_asset_ids: z.array(z.string()).optional(),
  source_project_id: z.string().nullable().optional(),
});
export type PortfolioItem = z.infer<typeof portfolioItemSchema>;

export const registrationSchema = z.object({
  id: z.string().optional(),
  body: str,
  number: str,
  label: str,
  verified: z.boolean().catch(false).default(false),
});
export type Registration = z.infer<typeof registrationSchema>;

export const publicCompanySchema = z.object({
  slug: z.string(),
  organization_id: z.string(),
  name: str,
  trading_name: str,
  kind: str,
  tagline: str,
  about: str,
  county: str,
  service_areas: strings,
  specialities: strings,
  year_established: nullableNum,
  years_in_business: nullableNum,
  team_size: str,
  nca_category: str,
  website: str,
  logo: imageSchema.nullable().catch(null).default(null),
  cover: imageSchema.nullable().catch(null).default(null),
  gallery: z.array(imageSchema).catch([]).default([]),
  registrations: z.array(registrationSchema).catch([]).default([]),
  badges: badgesSchema.catch(badgesSchema.parse({})),
  ratings: ratingSummarySchema.catch(ratingSummarySchema.parse({})),
  portfolio: z.array(portfolioItemSchema).catch([]).default([]),
  supplier: z
    .object({ categories: strings, offers: num, delivers: z.boolean().catch(false) })
    .optional(),
  published_at: nullableStr,
});
export type PublicCompany = z.infer<typeof publicCompanySchema>;

export async function fetchPublicCompany(slug: string): Promise<PublicCompany> {
  const response = await api.get<unknown>(`/public/companies/${encodeURIComponent(slug)}`);
  return publicCompanySchema.parse(response.data);
}

// -- the directory -----------------------------------------------------------------------

export const directoryCardSchema = z.object({
  slug: z.string(),
  organization_id: z.string(),
  name: str,
  kind: str,
  tagline: str,
  county: str,
  service_areas: strings,
  specialities: strings,
  nca_category: str,
  team_size: str,
  year_established: nullableNum,
  verified: z.boolean().catch(false).default(false),
  badges: badgesSchema.catch(badgesSchema.parse({})),
  rating_average: nullableNum,
  rating_count: num,
  logo: imageSchema.nullable().catch(null).default(null),
  shortlisted: z.boolean().optional(),
});
export type DirectoryCard = z.infer<typeof directoryCardSchema>;

export type PageMeta = { page: number; page_size: number; total: number; pages: number };

function pageMeta(meta: ApiMeta): PageMeta {
  return {
    page: Number(meta['page'] ?? 1),
    page_size: Number(meta['page_size'] ?? 20),
    total: Number(meta['total'] ?? 0),
    pages: Number(meta['pages'] ?? 0),
  };
}

export type DirectorySearch = {
  q?: string;
  kind?: string;
  speciality?: string;
  county?: string;
  min_rating?: number | null;
  nca_category?: string;
  verified?: boolean;
  sort?: 'rating' | 'name' | 'recent';
  page?: number;
  page_size?: number;
  for_org?: string | null;
};

export async function searchDirectory(
  search: DirectorySearch,
): Promise<{ rows: DirectoryCard[]; meta: PageMeta }> {
  const response = await api.get<unknown[]>('/directory/companies', {
    query: { ...search, verified: search.verified ? true : undefined },
  });
  return {
    rows: (response.data ?? []).flatMap((row) => directoryCardSchema.safeParse(row).data ?? []),
    meta: pageMeta(response.meta),
  };
}

// -- the company's own page ----------------------------------------------------------------

export const managePageSchema = z.object({
  available: z.boolean().catch(false).default(false),
  can_manage: z.boolean().catch(false).default(false),
  page: z.object({
    slug: str,
    status: z.string().catch('draft').default('draft'),
    published_at: nullableStr,
    tagline: str,
    cover: imageSchema.nullable().catch(null).default(null),
    gallery: z.array(imageSchema).catch([]).default([]),
    show_website: z.boolean().catch(true).default(true),
    exists: z.boolean().catch(false).default(false),
  }),
  missing: strings,
  registrations: z.array(registrationSchema).catch([]).default([]),
  badges: badgesSchema.catch(badgesSchema.parse({})),
  ratings: ratingSummarySchema.catch(ratingSummarySchema.parse({})),
  portfolio_count: num,
});
export type ManagePage = z.infer<typeof managePageSchema>;

const org = (orgId: string) => `/organizations/${orgId}`;

export async function fetchManagePage(orgId: string): Promise<ManagePage> {
  const response = await api.get<unknown>(`${org(orgId)}/public-profile`);
  return managePageSchema.parse(response.data);
}

export type PageChanges = Partial<{
  slug: string;
  tagline: string;
  cover_asset_id: string | null;
  gallery_asset_ids: string[];
  show_website: boolean;
}>;

export async function updateManagePage(
  orgId: string,
  changes: PageChanges,
  idempotencyKey: string,
): Promise<ManagePage> {
  const response = await api.patch<unknown>(
    `${org(orgId)}/public-profile`,
    { changes },
    key(idempotencyKey),
  );
  return managePageSchema.parse(response.data);
}

export async function publishPage(
  orgId: string,
  publish: boolean,
  idempotencyKey: string,
): Promise<ManagePage> {
  const response = await api.post<unknown>(
    `${org(orgId)}/public-profile/publish`,
    { publish },
    key(idempotencyKey),
  );
  return managePageSchema.parse(response.data);
}

export async function saveRegistration(
  orgId: string,
  id: string | null,
  data: { body?: string; number?: string; label?: string },
  idempotencyKey: string,
): Promise<Registration> {
  const response = id
    ? await api.patch<unknown>(`${org(orgId)}/registrations/${id}`, { data }, key(idempotencyKey))
    : await api.post<unknown>(`${org(orgId)}/registrations`, { data }, key(idempotencyKey));
  return registrationSchema.parse(response.data);
}

export async function removeRegistration(
  orgId: string,
  id: string,
  idempotencyKey: string,
): Promise<void> {
  await api.delete(`${org(orgId)}/registrations/${id}`, key(idempotencyKey));
}

// -- portfolio -------------------------------------------------------------------------------

export async function fetchPortfolio(orgId: string): Promise<PortfolioItem[]> {
  const response = await api.get<unknown[]>(`${org(orgId)}/portfolio`);
  return (response.data ?? []).flatMap((row) => portfolioItemSchema.safeParse(row).data ?? []);
}

export const buildProjectSchema = z.object({
  project_id: z.string(),
  title: str,
  status: str,
  county: str,
  year: nullableNum,
  role: str,
  value_band: str,
  in_portfolio: z.boolean().catch(false).default(false),
});
export type BuildProject = z.infer<typeof buildProjectSchema>;

export async function fetchBuildProjects(orgId: string): Promise<BuildProject[]> {
  const response = await api.get<unknown[]>(`${org(orgId)}/portfolio/build-projects`);
  return (response.data ?? []).flatMap((row) => buildProjectSchema.safeParse(row).data ?? []);
}

export type PortfolioInput = Partial<{
  title: string;
  county: string;
  location: string;
  year: number | null;
  value_band: string;
  role: string;
  scope: string;
  photo_asset_ids: string[];
  testimonial: string;
  testimonial_by: string;
  visible: boolean;
  source_project_id: string;
}>;

export async function savePortfolioItem(
  orgId: string,
  id: string | null,
  data: PortfolioInput,
  idempotencyKey: string,
): Promise<PortfolioItem> {
  const response = id
    ? await api.patch<unknown>(
        `${org(orgId)}/portfolio/${id}`,
        { changes: data },
        key(idempotencyKey),
      )
    : await api.post<unknown>(`${org(orgId)}/portfolio`, { data }, key(idempotencyKey));
  return portfolioItemSchema.parse(response.data);
}

export async function removePortfolioItem(
  orgId: string,
  id: string,
  idempotencyKey: string,
): Promise<void> {
  await api.delete(`${org(orgId)}/portfolio/${id}`, key(idempotencyKey));
}

// -- shortlist -------------------------------------------------------------------------------

export const shortlistRowSchema = z.object({
  id: z.string(),
  /** A company, or a person from Professionals (their listing). */
  type: z.enum(['company', 'individual']).catch('company').default('company'),
  company_id: z.string().nullable().catch(null).default(null),
  listing_id: z.string().nullable().catch(null).default(null),
  note: str,
  added_at: nullableStr,
  published: z.boolean().catch(false).default(false),
  company: z
    .object({
      name: str,
      kind: str,
      slug: z.string().nullable().catch(null).default(null),
      tagline: str.optional(),
      county: str.optional(),
      rating_average: nullableNum.optional(),
      rating_count: num.optional(),
      verified: z.boolean().optional(),
    })
    .optional()
    .catch(undefined),
  /** A person: their card while listed, else just their name. */
  person: z
    .object({
      name: str,
      slug: z.string().nullable().catch(null).default(null),
      headline: str.optional(),
      county: z.string().nullable().optional().catch(undefined),
      trades: z.array(z.string()).optional().catch(undefined),
      rating_average: nullableNum.optional(),
      rating_count: num.optional(),
    })
    .optional()
    .catch(undefined),
});
export type ShortlistRow = z.infer<typeof shortlistRowSchema>;

/** A company's shortlist, or the person's own (`null`: an individual owner, `/me/shortlist`). */
const shortlistOwner = (orgId: string | null) => (orgId ? org(orgId) : '/me');

export async function fetchShortlist(
  orgId: string | null,
): Promise<{ rows: ShortlistRow[]; canManage: boolean }> {
  const response = await api.get<unknown[]>(`${shortlistOwner(orgId)}/shortlist`);
  return {
    rows: (response.data ?? []).flatMap((row) => shortlistRowSchema.safeParse(row).data ?? []),
    canManage: response.meta['can_manage'] === true,
  };
}

export async function addToShortlist(
  orgId: string | null,
  companyId: string,
  note: string,
  idempotencyKey: string,
): Promise<void> {
  await api.post(
    `${shortlistOwner(orgId)}/shortlist`,
    { company_id: companyId, note },
    key(idempotencyKey),
  );
}

export async function noteShortlist(
  orgId: string | null,
  companyId: string,
  note: string,
  idempotencyKey: string,
): Promise<void> {
  await api.patch(`${shortlistOwner(orgId)}/shortlist/${companyId}`, { note }, key(idempotencyKey));
}

export async function removeFromShortlist(
  orgId: string | null,
  companyId: string,
  idempotencyKey: string,
): Promise<void> {
  await api.delete(`${shortlistOwner(orgId)}/shortlist/${companyId}`, key(idempotencyKey));
}

// -- read models -----------------------------------------------------------------------------

export const companyProjectSchema = z.object({
  project_id: z.string(),
  name: str,
  code: str,
  status: str,
  roles: strings,
  county: str,
  value_band: str,
  progress_percent: num,
  start_date: nullableStr,
  target_completion_date: nullableStr,
  handed_over: z.boolean().catch(false).default(false),
  is_member: z.boolean().catch(false).default(false),
});
export type CompanyProject = z.infer<typeof companyProjectSchema>;

export async function fetchCompanyProjects(
  orgId: string,
): Promise<{ rows: CompanyProject[]; allProjects: boolean }> {
  const response = await api.get<unknown[]>(`${org(orgId)}/projects`);
  return {
    rows: (response.data ?? []).flatMap((row) => companyProjectSchema.safeParse(row).data ?? []),
    allProjects: response.meta['all_projects'] === true,
  };
}

export const clientSchema = z.object({
  organization_id: z.string(),
  name: str,
  kind: str,
  projects: num,
  active: num,
  slug: nullableStr,
  last_activity: nullableStr,
  /** An individual owner (name is "<Name> (owner)"): never a company, no profile. */
  individual: z.boolean().catch(false).default(false),
});
export type CompanyClient = z.infer<typeof clientSchema>;

export async function fetchClients(orgId: string): Promise<CompanyClient[]> {
  const response = await api.get<unknown[]>(`${org(orgId)}/clients`);
  return (response.data ?? []).flatMap((row) => clientSchema.safeParse(row).data ?? []);
}

export const workItemSchema = z.object({
  type: str,
  id: z.string(),
  project: z.object({ id: z.string(), name: str }),
  title: str,
  due: nullableStr,
  status: str,
  overdue: z.boolean().catch(false).default(false),
});
export type WorkItem = z.infer<typeof workItemSchema>;
export const workloadSchema = z.object({
  items: z.array(workItemSchema).catch([]).default([]),
  counts: z.record(z.string(), z.number()).catch({}).default({}),
  overdue: num,
  projects: num,
});
export type Workload = z.infer<typeof workloadSchema>;

export async function fetchWorkload(orgId: string): Promise<Workload> {
  const response = await api.get<unknown>(`${org(orgId)}/workload`);
  return workloadSchema.parse(response.data);
}

export const roleMatrixSchema = z.object({
  kind: str,
  roles: z
    .array(z.object({ role: z.string(), permissions: strings }))
    .catch([])
    .default([]),
  permissions: strings,
  my_role: str,
});
export type RoleMatrix = z.infer<typeof roleMatrixSchema>;

export async function fetchRoleMatrix(orgId: string): Promise<RoleMatrix> {
  const response = await api.get<unknown>(`${org(orgId)}/roles`);
  return roleMatrixSchema.parse(response.data);
}

// -- fee tracker ---------------------------------------------------------------------------------

export const feeStageSchema = z.object({
  id: z.string(),
  name: str,
  sequence: num,
  amount_minor: num,
  invoiced_minor: num,
  invoiced_on: nullableStr,
  invoice_reference: str,
  paid_minor: num,
  paid_on: nullableStr,
  /** pending | invoiced | part_paid | paid */
  status: z.string().catch('pending').default('pending'),
});
export type FeeStage = z.infer<typeof feeStageSchema>;

export const feeAgreementSchema = z.object({
  id: z.string(),
  title: str,
  client_name: str,
  project_id: nullableStr,
  project_name: nullableStr,
  basis: z.string().catch('percentage').default('percentage'),
  rate_bp: nullableNum,
  construction_value_minor: nullableNum,
  fee_minor: num,
  currency: z.string().catch('KES').default('KES'),
  status: z.string().catch('active').default('active'),
  notes: str,
  invoiced_minor: num,
  paid_minor: num,
  outstanding_minor: num,
  unbilled_minor: num,
  stages: z.array(feeStageSchema).catch([]).default([]),
});
export type FeeAgreement = z.infer<typeof feeAgreementSchema>;

export const feeTrackerSchema = z.object({
  agreements: z.array(feeAgreementSchema).catch([]).default([]),
  totals: z
    .array(
      z.object({
        currency: z.string(),
        fee_minor: num,
        invoiced_minor: num,
        paid_minor: num,
      }),
    )
    .catch([])
    .default([]),
  projects: z
    .array(z.object({ id: z.string(), name: str }))
    .catch([])
    .default([]),
});
export type FeeTracker = z.infer<typeof feeTrackerSchema>;

export async function fetchFees(orgId: string): Promise<FeeTracker> {
  const response = await api.get<unknown>(`${org(orgId)}/fees`);
  return feeTrackerSchema.parse({
    ...(response.data as object),
    projects: response.meta['projects'],
  });
}

export type FeeInput = Partial<{
  title: string;
  client_name: string;
  project_id: string | null;
  basis: string;
  rate_bp: number | null;
  construction_value_minor: number | null;
  fee_minor: number;
  currency: string;
  status: string;
  notes: string;
}>;

export async function saveFeeAgreement(
  orgId: string,
  id: string | null,
  data: FeeInput,
  idempotencyKey: string,
): Promise<FeeAgreement> {
  const response = id
    ? await api.patch<unknown>(`${org(orgId)}/fees/${id}`, { changes: data }, key(idempotencyKey))
    : await api.post<unknown>(`${org(orgId)}/fees`, { data }, key(idempotencyKey));
  return feeAgreementSchema.parse(response.data);
}

export async function removeFeeAgreement(
  orgId: string,
  id: string,
  idempotencyKey: string,
): Promise<void> {
  await api.delete(`${org(orgId)}/fees/${id}`, key(idempotencyKey));
}

export type StageInput = Partial<{
  name: string;
  share_bp: number | null;
  amount_minor: number;
  invoiced_minor: number;
  invoiced_on: string | null;
  invoice_reference: string;
  paid_minor: number;
  paid_on: string | null;
}>;

export async function saveFeeStage(
  orgId: string,
  agreementId: string,
  stageId: string | null,
  data: StageInput,
  idempotencyKey: string,
): Promise<FeeAgreement> {
  const base = `${org(orgId)}/fees/${agreementId}/stages`;
  const response = stageId
    ? await api.patch<unknown>(`${base}/${stageId}`, { changes: data }, key(idempotencyKey))
    : await api.post<unknown>(base, { data }, key(idempotencyKey));
  return feeAgreementSchema.parse(response.data);
}

export async function removeFeeStage(
  orgId: string,
  agreementId: string,
  stageId: string,
  idempotencyKey: string,
): Promise<void> {
  await api.delete(`${org(orgId)}/fees/${agreementId}/stages/${stageId}`, key(idempotencyKey));
}

// -- project invitations addressed to a company -----------------------------------------------

export const companyInvitationSchema = z.object({
  id: z.string(),
  project: z.object({ name: str, county: str, status: str }),
  party_role: str,
  role: str,
  invited_by_company: str,
  expires_at: nullableStr,
  created_at: nullableStr,
});
export type CompanyInvitation = z.infer<typeof companyInvitationSchema>;

export async function fetchCompanyInvitations(orgId: string): Promise<CompanyInvitation[]> {
  const response = await api.get<unknown[]>(`${org(orgId)}/project-invitations`);
  return (response.data ?? []).flatMap((row) => companyInvitationSchema.safeParse(row).data ?? []);
}

export async function answerCompanyInvitation(
  orgId: string,
  invitationId: string,
  accept: boolean,
  idempotencyKey: string,
): Promise<{ project_id?: string }> {
  const response = await api.post<{ project_id?: string }>(
    `${org(orgId)}/project-invitations/${invitationId}/${accept ? 'accept' : 'decline'}`,
    undefined,
    key(idempotencyKey),
  );
  return response.data ?? {};
}

export async function inviteCompanyToProject(
  projectId: string,
  organizationId: string,
  partyRole: string,
  idempotencyKey: string,
): Promise<void> {
  await api.post(
    `/projects/${projectId}/company-invitations`,
    { organization_id: organizationId, party_role: partyRole },
    key(idempotencyKey),
  );
}

// -- the person ----------------------------------------------------------------------------------

export const personalProfileSchema = z.object({
  name: str,
  email: str,
  phone: str,
  avatar_url: str,
  title: str,
  bio: str,
  languages: strings,
  registrations: z
    .array(z.object({ body: z.string(), number: z.string() }))
    .catch([])
    .default([]),
});
export type PersonalProfile = z.infer<typeof personalProfileSchema>;

export async function fetchPersonalProfile(): Promise<PersonalProfile> {
  const response = await api.get<unknown>('/me/professional-profile');
  return personalProfileSchema.parse(response.data);
}

export async function savePersonalProfile(
  data: Partial<Pick<PersonalProfile, 'title' | 'bio' | 'languages' | 'registrations'>>,
  idempotencyKey: string,
): Promise<PersonalProfile> {
  const response = await api.put<unknown>(
    '/me/professional-profile',
    { data },
    key(idempotencyKey),
  );
  return personalProfileSchema.parse(response.data);
}

export const ONBOARDING_FLOWS = [
  'contractor',
  'subcontractor',
  'consultant',
  'qs',
  'owner',
  'supplier',
] as const;
export type OnboardingFlow = (typeof ONBOARDING_FLOWS)[number];

export const onboardingProgressSchema = z.object({
  flow: z.string(),
  step: str,
  steps: strings,
  completed_steps: strings,
  status: z.string().catch('in_progress').default('in_progress'),
  completed_at: nullableStr,
  organization: z
    .object({ id: z.string(), name: str, kind: str })
    .nullable()
    .catch(null)
    .default(null),
  /** Still in progress, but the person now works through another company: never offered again. */
  abandoned: z.boolean().catch(false).default(false),
});
export type OnboardingProgress = z.infer<typeof onboardingProgressSchema>;

export const onboardingOverviewSchema = z.object({
  flows: z.array(onboardingProgressSchema).catch([]).default([]),
  definitions: z.record(z.string(), z.array(z.string())).catch({}).default({}),
  resume: nullableStr,
  suggest: z.boolean().catch(false).default(false),
});
export type OnboardingOverview = z.infer<typeof onboardingOverviewSchema>;

export async function fetchOnboarding(): Promise<OnboardingOverview> {
  const response = await api.get<unknown>('/me/onboarding');
  return onboardingOverviewSchema.parse(response.data);
}

export async function startOnboarding(
  flow: OnboardingFlow,
  idempotencyKey: string,
): Promise<OnboardingProgress> {
  const response = await api.post<unknown>(
    `/me/onboarding/${flow}/start`,
    undefined,
    key(idempotencyKey),
  );
  return onboardingProgressSchema.parse(response.data);
}

export async function onboardCompany(
  flow: OnboardingFlow,
  body: {
    name?: string;
    kind?: string;
    profile?: Record<string, unknown>;
    organization_id?: string;
  },
  idempotencyKey: string,
): Promise<OnboardingProgress> {
  const response = await api.post<unknown>(
    `/me/onboarding/${flow}/company`,
    body,
    key(idempotencyKey),
  );
  return onboardingProgressSchema.parse(response.data);
}

export async function completeOnboardingStep(
  flow: OnboardingFlow,
  step: string,
  idempotencyKey: string,
  goTo = '',
): Promise<OnboardingProgress> {
  const response = await api.post<unknown>(
    `/me/onboarding/${flow}/steps`,
    { step, go_to: goTo },
    key(idempotencyKey),
  );
  return onboardingProgressSchema.parse(response.data);
}

export async function finishOnboarding(
  flow: OnboardingFlow,
  skipped: boolean,
  idempotencyKey: string,
): Promise<OnboardingProgress> {
  const response = await api.post<unknown>(
    `/me/onboarding/${flow}/finish`,
    { skipped },
    key(idempotencyKey),
  );
  return onboardingProgressSchema.parse(response.data);
}

// -- KorOS staff ------------------------------------------------------------------------------------

export const adminRegistrationSchema = registrationSchema.extend({
  id: z.string(),
  organization: z.object({ id: z.string(), name: str, kind: str }),
});
export type AdminRegistration = z.infer<typeof adminRegistrationSchema>;

export async function fetchAdminRegistrations(verified: boolean): Promise<AdminRegistration[]> {
  const response = await api.get<unknown[]>('/admin/registrations', { query: { verified } });
  return (response.data ?? []).flatMap((row) => adminRegistrationSchema.safeParse(row).data ?? []);
}

export async function verifyRegistration(
  id: string,
  verified: boolean,
  idempotencyKey: string,
): Promise<void> {
  await api.post(`/admin/registrations/${id}/verify`, { verified }, key(idempotencyKey));
}
